import { useMutation } from "@apollo/client";
import { useFormik } from "formik";
import moment from "moment";
import PropTypes from "prop-types";
import { Button, Toasts } from "hireart-components";
import { UPDATE_ASSIGNMENT_TERMS } from "../../../Managers/People/mutations/UpdateAssignmentTerms";
import UserQuery from "../../../Managers/People/queries/UserQuery";
import PersonQuery from "../../../StaffingPartners/Vms/queries/PersonQuery";
import { Modal } from "../Modal";
import { ContractLengthForm } from "./ContractLengthForm";
import { FORM_FIELD, validationSchema } from "./form-config";

export const ContractLengthModal = ({
  hasModalOpen,
  onModalClose,
  token,
  startDate = "",
  endDate = "",
  isStaffingPartner,
}) => {
  const initialValues = {
    [FORM_FIELD.END_DATE]: endDate || "",
    [FORM_FIELD.START_DATE]: startDate || "",
    [FORM_FIELD.DESCRIPTION]: "",
  };
  const [updateAssignmentTerms] = useMutation(UPDATE_ASSIGNMENT_TERMS, {
    awaitRefetchQueries: true,
    fetchPolicy: "network-only",
    refetchQueries: [isStaffingPartner ? PersonQuery : UserQuery],
    onCompleted: () => {
      onModalClose();
      Toasts.success("Contract dates sucessfully updated.");
    },
    onError: () =>
      Toasts.error(
        "An error was encountered while attempting to update the contract dates. Please try again later."
      ),
  });

  const { handleSubmit, setFieldValue, isSubmitting, isValid, dirty, values } =
    useFormik({
      initialValues,
      onSubmit: async (newValues, { setSubmitting, resetForm }) => {
        setSubmitting(true);

        const { data } = await updateAssignmentTerms({
          variables: {
            token,
            assignment_terms_input: {
              start_date: moment(newValues[FORM_FIELD.START_DATE]).format("L"),
              end_date: newValues[FORM_FIELD.END_DATE]
                ? moment(newValues[FORM_FIELD.END_DATE]).format("L")
                : null,
              description: newValues[FORM_FIELD.DESCRIPTION],
            },
          },
        });

        const persistedData = data?.updateAssignmentTerms || {};

        resetForm({
          values: {
            [FORM_FIELD.END_DATE]: persistedData[FORM_FIELD.END_DATE] || "",
            [FORM_FIELD.START_DATE]: persistedData[FORM_FIELD.START_DATE] || "",
            [FORM_FIELD.DESCRIPTION]: "",
          },
        });

        setSubmitting(false);
      },
      validationSchema,
    });

  const contractLengthProps = {
    onSetFieldValue: setFieldValue,
    onSubmit: handleSubmit,
    values,
  };

  return (
    <Modal open={hasModalOpen} size="small">
      <Modal.Header onClose={onModalClose} closeIcon>
        Edit contract dates
      </Modal.Header>
      <Modal.Content>
        <ContractLengthForm {...contractLengthProps} />
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onModalClose} content="Cancel" outlined size="lg" />
        <Button
          loading={isSubmitting}
          disabled={!dirty || isSubmitting || !isValid}
          form="contract-length-form"
          type="submit"
          content="Save"
          size="lg"
        />
      </Modal.Actions>
    </Modal>
  );
};

ContractLengthModal.propTypes = {
  hasModalOpen: PropTypes.bool.isRequired,
  onModalClose: PropTypes.func.isRequired,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  token: PropTypes.string.isRequired,
  isStaffingPartner: PropTypes.bool.isRequired,
};
